import { Injectable, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { AuthService } from '../../modules/auth/services/auth.service';
import { JwtService } from '../../core/services/jwt.service';
import { PermissionService } from '../../core/services/permission.service';
import { PREMISSIONS } from '../../core/enums/permissions';

/** مستوى الخبرة - يُستخدم لتخصيص تجربة المستخدم (تلميحات، شروحات، خيارات متقدمة) */
export type ExperienceLevel = 'beginner' | 'intermediate' | 'expert';

/** تصنيف المستخدم - الدور في المنشأة */
export type UserRole = 'owner' | 'accountant' | 'technician' | 'trainee';

export interface Stage1Data {
  role: UserRole;
  experienceLevel: ExperienceLevel;
}

/** مهمة في المرحلة 4 - تُعرض حسب الدور */
export interface Stage4Task {
  id: string;
  path: string;
  icon: string;
  permissionKey?: string;
}

const STORAGE_KEY_PREFIX = 'smart_onboarding_';
const STAGE4_KEY_SUFFIX = '_stage4_done';
const STAGE4_TASKS_KEY_SUFFIX = '_stage4_tasks';
const WELCOME_SHOWN_KEY_SUFFIX = '_welcome_shown';
const LEGACY_KEY_PREFIX = 'smart_onboarding_experience_';
const COACHMARKS_SKIP_KEY_PREFIX = 'smart_onboarding_coachmarks_skipped_';
const OWNER_MANDATORY_FLOW_SUSPENDED_PREFIX = 'smart_onboarding_owner_mandatory_flow_suspended_';
const STAGE4_QUICKSTART_EXIT_SUSPENDED_PREFIX = 'smart_onboarding_stage4_quickstart_exit_suspended_';
const DEFAULT_LEVEL: ExperienceLevel = 'intermediate';
const DEFAULT_ROLE: UserRole = 'owner';

/**
 * بادئات مسارات مهام الإعداد الإلزامي / «بداية سريعة».
 * الانتقال منها إلى لوحة التحكم يدوياً يعلّق المودال والمعالجات حتى الاستئناف من الإعدادات.
 */
const ONBOARDING_TASK_SURFACE_PREFIXES: readonly string[] = [
  '/stock/products',
  '/sales/customers',
  '/sales/sales-invoices',
  '/purchases/vendors',
  '/purchases/purchases-invoices',
  '/report/stock-movement',
  '/settings/currencies',
  '/settings/currency',
  '/settings/currentcompany',
  '/stock/stores',
  '/stock/product-categories',
  '/stock/products-barcode',
  '/accounting/accounts',
  '/accounting/journals',
  '/open-balance',
  '/report/trail-balance',
  '/accounting/receipt-vouchers',
  '/accounting/payment-vouchers',
  '/accounting/closing-management',
  '/report/simple-account-statement',
  '/shifts',
  '/pos-invoices',
  '/zatca/zatcaunits',
  '/users',
  '/roles',
  '/attachments',
  '/settings/general-settings',
];

/** مراحل المعالج (1 و 4 فقط - تم تجاوز 2 و 3) */
export const ONBOARDING_TOTAL_STAGES = 2;
export const ONBOARDING_STAGE4_TASK_WEIGHT = 1 / 3; // كل مهمة = ثلث مرحلة 4

/** صاحب منشأة — مبتدئ / متوسط: خمس مهام بعد الإعداد الإلزامي */
const OWNER_STAGE4_TASKS_FULL: Stage4Task[] = [
  {
    id: 'add_product',
    path: '/stock/products/list-products',
    icon: 'fas fa-box-open',
    permissionKey: PREMISSIONS.Products.View,
  },
  {
    id: 'add_customer',
    path: '/sales/customers/customers-list',
    icon: 'fas fa-user-plus',
    permissionKey: PREMISSIONS.Customer.View,
  },
  {
    id: 'add_sales_invoice',
    path: '/sales/sales-invoices/create-invoice/create/new',
    icon: 'fas fa-file-invoice',
    permissionKey: PREMISSIONS.Document.Sales.Create,
  },
  {
    id: 'add_vendor',
    path: '/purchases/vendors/vendors-list',
    icon: 'fas fa-truck',
    permissionKey: PREMISSIONS.Vendor.View,
  },
  {
    id: 'stock_movement_report',
    path: '/report/stock-movement',
    icon: 'fas fa-chart-bar',
    permissionKey: PREMISSIONS.StockMovement.View,
  },
];

/** صاحب منشأة — خبير: ثلاث مهام فقط */
const OWNER_STAGE4_TASKS_EXPERT: Stage4Task[] = [
  OWNER_STAGE4_TASKS_FULL[0],
  OWNER_STAGE4_TASKS_FULL[1],
  OWNER_STAGE4_TASKS_FULL[3],
];

/** محاسب — مبتدئ: ست مهام */
const ACCOUNTANT_STAGE4_TASKS_BEGINNER: Stage4Task[] = [
  {
    id: 'chart_of_accounts',
    path: '/accounting/accounts/list-accounts',
    icon: 'fas fa-book',
    permissionKey: PREMISSIONS.GLAccount.View,
  },
  {
    id: 'opening_balance',
    path: '/open-balance/list-open-balances',
    icon: 'fas fa-balance-scale',
    permissionKey: PREMISSIONS.OpeningBalance.View,
  },
  {
    id: 'trial_balance',
    path: '/report/trail-balance',
    icon: 'fas fa-columns',
    permissionKey: PREMISSIONS.TrialBalance.View,
  },
  {
    id: 'receipt_voucher',
    path: '/accounting/receipt-vouchers/list-receipt-vouchers',
    icon: 'fas fa-hand-holding-usd',
    permissionKey: PREMISSIONS.ReceiptVoucher.View,
  },
  {
    id: 'payment_voucher',
    path: '/accounting/payment-vouchers/list-payment-vouchers',
    icon: 'fas fa-money-bill-wave',
    permissionKey: PREMISSIONS.PaymentVoucher.View,
  },
  {
    id: 'account_statement',
    path: '/report/simple-account-statement',
    icon: 'fas fa-file-invoice-dollar',
    permissionKey: PREMISSIONS.AccountStatement.View,
  },
];

/** محاسب — متوسط: خمس مهام (بدون دليل الحسابات) */
const ACCOUNTANT_STAGE4_TASKS_INTERMEDIATE: Stage4Task[] =
  ACCOUNTANT_STAGE4_TASKS_BEGINNER.slice(1);

/** محاسب — خبير: ثلاث مهام */
const ACCOUNTANT_STAGE4_TASKS_EXPERT: Stage4Task[] = [
  ACCOUNTANT_STAGE4_TASKS_BEGINNER[1],
  ACCOUNTANT_STAGE4_TASKS_BEGINNER[2],
  {
    id: 'closing_management',
    path: '/accounting/closing-management',
    icon: 'fas fa-lock',
    permissionKey: PREMISSIONS.Closing.Search,
  },
];

const TECHNICIAN_TASK_ADD_USER: Stage4Task = {
  id: 'add_user',
  path: '/users/list/create-user/create/new',
  icon: 'fas fa-user-plus',
  permissionKey: PREMISSIONS.Users.Create,
};

const TECHNICIAN_TASK_EDIT_PROFILE: Stage4Task = {
  id: 'edit_profile',
  path: '/users/profile',
  icon: 'fas fa-user-edit',
  permissionKey: PREMISSIONS.Users.Update,
};

const TECHNICIAN_TASK_CREATE_ROLE: Stage4Task = {
  id: 'create_role',
  path: '/roles/create-update-role/create/new',
  icon: 'fas fa-user-shield',
  permissionKey: PREMISSIONS.Roles.Create,
};

const TECHNICIAN_TASK_UPLOAD_ATTACHMENT: Stage4Task = {
  id: 'upload_attachment',
  path: '/attachments',
  icon: 'fas fa-cloud-upload-alt',
  permissionKey: PREMISSIONS.Attachments.View,
};

const TECHNICIAN_TASK_GENERAL_SETTINGS: Stage4Task = {
  id: 'general_settings',
  path: '/settings/general-settings',
  icon: 'fas fa-cog',
  permissionKey: PREMISSIONS.GeneralSettings.View,
};

/** تقني — مبتدئ: خمس مهام */
const TECHNICIAN_STAGE4_TASKS_BEGINNER: Stage4Task[] = [
  TECHNICIAN_TASK_ADD_USER,
  TECHNICIAN_TASK_EDIT_PROFILE,
  TECHNICIAN_TASK_CREATE_ROLE,
  TECHNICIAN_TASK_UPLOAD_ATTACHMENT,
  TECHNICIAN_TASK_GENERAL_SETTINGS,
];

/** تقني — متوسط: ثلاث مهام */
const TECHNICIAN_STAGE4_TASKS_INTERMEDIATE: Stage4Task[] = [
  TECHNICIAN_TASK_ADD_USER,
  TECHNICIAN_TASK_CREATE_ROLE,
  TECHNICIAN_TASK_GENERAL_SETTINGS,
];

/** تقني — خبير: إعدادات عامة ثم إضافة مستخدم */
const TECHNICIAN_STAGE4_TASKS_EXPERT: Stage4Task[] = [
  TECHNICIAN_TASK_GENERAL_SETTINGS,
  TECHNICIAN_TASK_ADD_USER,
];

/** متدرب — قائمة ثابتة (لا يُفرَّق حسب مستوى الخبرة) */
const TRAINEE_STAGE4_TASKS: Stage4Task[] = [
  {
    id: 'create_sales_invoice',
    path: '/sales/sales-invoices/create-invoice/create/new',
    icon: 'fas fa-file-invoice',
    permissionKey: PREMISSIONS.Document.Sales.Create,
  },
  {
    id: 'trainee_add_product',
    path: '/stock/products/create-or-update-product/create/new',
    icon: 'fas fa-box-open',
    permissionKey: PREMISSIONS.Products.Create,
  },
  {
    id: 'trainee_trial_balance',
    path: '/report/trail-balance',
    icon: 'fas fa-columns',
    permissionKey: PREMISSIONS.TrialBalance.View,
  },
  {
    id: 'open_shift',
    path: '/shifts/create-update-shift/create/new',
    icon: 'fas fa-clock',
    permissionKey: PREMISSIONS.Shift.Create,
  },
  {
    id: 'pos',
    path: '/pos-invoices/create-invoice/create/new',
    icon: 'fas fa-cash-register',
    permissionKey: PREMISSIONS.Document.Sales.Create,
  },
];

/** مهام المرحلة 4 حسب الدور – متوافقة مع تحليل Smart_Onboarding */
const ROLE_TASKS: Record<UserRole, Stage4Task[]> = {
  owner: OWNER_STAGE4_TASKS_FULL,

  /** افتراضي للمحاسب عند عدم تمرير المستوى — يُستبدل في getRoleBasedTasks */
  accountant: ACCOUNTANT_STAGE4_TASKS_INTERMEDIATE,

  /** افتراضي للتقني — يُستبدل في getRoleBasedTasks */
  technician: TECHNICIAN_STAGE4_TASKS_INTERMEDIATE,

  trainee: TRAINEE_STAGE4_TASKS,
};

@Injectable({ providedIn: 'root' })
export class UserExperienceLevelService {
  private readonly authService = inject(AuthService);
  private readonly jwtService = inject(JwtService);
  private readonly permission = inject(PermissionService);

  private readonly reopenStage1Subject = new Subject<void>();
  /** للـ Layout: إعادة عرض مودال تخصيص التجربة بعد إعادة التعيين من الإعدادات */
  readonly reopenStage1ModalRequests$ = this.reopenStage1Subject.asObservable();

  private readonly stage1Signal = signal<Stage1Data | null>(null);
  /** true عندما يختار المستخدم «تخطي» أو إغلاق مودال المرحلة 1 — لا تُفعَّل المعالجات الإرشادية */
  private readonly coachmarksSkippedSignal = signal(false);
  /** true عند تعليق مسار الإعداد الإلزامي (صاحب منشأة) بسبب الخروج عن المسار — يُعرَّف من OnboardingJourneyManagerService */
  private readonly ownerMandatoryFlowSuspendedSignal = signal(false);
  /** خروج يدوي من شاشات «بداية سريعة»/مهام الإعداد نحو لوحة التحكم — يعطّل المودال والمعالجات حتى الاستئناف */
  private readonly stage4QuickStartExitSuspendedSignal = signal(false);

  constructor() {
    const boot = this.loadInitialBootstrap();
    this.stage1Signal.set(boot.stage1);
    this.coachmarksSkippedSignal.set(boot.coachmarksSkipped);
    this.ownerMandatoryFlowSuspendedSignal.set(this.loadOwnerMandatorySuspendedFromStorage());
    this.stage4QuickStartExitSuspendedSignal.set(this.loadStage4QuickStartExitSuspendedFromStorage());
  }

  /** بعد مسح مفاتيح التعليق من localStorage (إعدادات المعالج الذكي). */
  reloadSuspendedFlagsFromStorage(): void {
    this.ownerMandatoryFlowSuspendedSignal.set(this.loadOwnerMandatorySuspendedFromStorage());
    this.stage4QuickStartExitSuspendedSignal.set(this.loadStage4QuickStartExitSuspendedFromStorage());
  }

  /**
   * بعد توفر JWT (تسجيل دخول أو تحديث توكن): إعادة قراءة التخزين المحلي.
   * بدون ذلك قد لا يُحمَّل «تخطي المعالجات الإرشادية» إذا تهيَّأت الخدمة قبل وجود توكن (مثل فتح صفحة الدخول بعد تحديث كامل).
   */
  syncFromStorageAfterAuthentication(): void {
    const boot = this.loadInitialBootstrap();
    this.stage1Signal.set(boot.stage1);
    this.coachmarksSkippedSignal.set(boot.coachmarksSkipped);
    this.reloadSuspendedFlagsFromStorage();
  }

  /** للقالب / إعدادات المعالج: مودال «بداية سريعة» مُعلَّق بسبب خروج يدوي */
  readonly isStage4QuickStartExitSuspended = computed(() => this.stage4QuickStartExitSuspendedSignal());

  /**
   * تطبيع مسار التنقل (للمقارنة مع بادئات المهام ولوحة التحكم).
   */
  normalizeNavigationUrl(router: Router, rawUrl: string): string {
    try {
      const tree = router.parseUrl(rawUrl);
      const serialized = router.serializeUrl(tree);
      return (serialized.split('?')[0] || '').toLowerCase();
    } catch {
      const p = rawUrl.split('?')[0];
      const withSlash = p.startsWith('/') ? p : `/${p}`;
      return withSlash.toLowerCase();
    }
  }

  /** هل الوجهة تعتبر لوحة التحكم الرئيسية؟ */
  isPrimaryDashboardUrl(normalizedPath: string): boolean {
    const p = normalizedPath.split('?')[0].toLowerCase();
    return p === '/dashboard' || p === '/' || p === '';
  }

  /** هل المسار ضمن شاشات مهام الإعداد أو «بداية سريعة»؟ */
  urlMatchesOnboardingTaskSurface(normalizedPath: string): boolean {
    const p = normalizedPath.split('?')[0].toLowerCase();
    if (this.isPrimaryDashboardUrl(p)) {
      return false;
    }
    return ONBOARDING_TASK_SURFACE_PREFIXES.some((pre) => p.startsWith(pre));
  }

  /**
   * هل ما زال مودال «بداية سريعة» يحق أن يُعرض نظرياً (دون اعتبار تعليق الخروج)؟
   */
  isQuickStartStage4ModalPendingExcludingExitFlag(): boolean {
    if (!this.isStage1Complete() || this.coachmarksSkippedSignal()) {
      return false;
    }
    if (this.effectiveLevel() === 'beginner') {
      return false;
    }
    if (this.getRoleBasedTasks(this.effectiveRole(), this.effectiveLevel()).length === 0) {
      return false;
    }
    if (this.ownerMandatoryFlowSuspendedSignal()) {
      return false;
    }
    const key = this.getStage4StorageKey();
    if (!key) {
      return false;
    }
    try {
      return localStorage.getItem(key) !== 'true';
    } catch {
      return false;
    }
  }

  setStage4QuickStartExitSuspended(value: boolean): void {
    this.stage4QuickStartExitSuspendedSignal.set(value);
    try {
      const key = this.getStage4QuickStartExitSuspendedKey();
      if (!key) return;
      if (value) {
        localStorage.setItem(key, '1');
      } else {
        localStorage.removeItem(key);
      }
    } catch {
      /* ignore */
    }
  }

  /**
   * يُستدعى من مسار الإعداد الإلزامي فقط — يعطّل المعالجات ومودال «بداية سريعة» أثناء التعليق.
   */
  setOwnerMandatoryFlowSuspended(value: boolean): void {
    this.ownerMandatoryFlowSuspendedSignal.set(value);
    try {
      const key = this.getOwnerMandatoryFlowSuspendedKey();
      if (!key) return;
      if (value) {
        localStorage.setItem(key, '1');
      } else {
        localStorage.removeItem(key);
      }
    } catch {
      /* ignore */
    }
  }

  /** المعالجات الإرشادية (الـ coach marks) تعمل فقط بعد «متابعة» من مودال الدور/المستوى وليس بعد التخطي */
  readonly areSmartWizardsGloballyEnabled = computed(
    () =>
      !this.coachmarksSkippedSignal() &&
      this.stage1Signal() !== null &&
      !this.ownerMandatoryFlowSuspendedSignal() &&
      !this.stage4QuickStartExitSuspendedSignal()
  );

  /** للواجهات: هل اختار المستخدم تخطي/إغلاق مودال التخصيص؟ */
  readonly hasSkippedCoachmarksOnboarding = computed(() => this.coachmarksSkippedSignal());

  /** الدور الحالي (صاحب منشأة، محاسب، تقني، متدرب) */
  readonly userRole = computed(() => this.stage1Signal()?.role ?? null);

  /** المستوى الحالي (أو null إذا لم يُختر بعد) */
  readonly experienceLevel = computed(() => this.stage1Signal()?.experienceLevel ?? null);

  /** هل تم إكمال المرحلة 1 (الدور + المستوى)؟ */
  readonly isStage1Complete = computed(() => this.stage1Signal() !== null);

  /** هل يجب عرض المرحلة 4 (التجربة المصغرة)؟ تُعرض بعد إكمال المرحلة 1 */
  shouldShowStage4(): boolean {
    if (this.ownerMandatoryFlowSuspendedSignal()) return false;
    if (this.stage4QuickStartExitSuspendedSignal()) return false;
    if (!this.isStage1Complete()) return false;
    // المبتدئ يحصل على جولة إرشادية أقوى داخل الشاشات، وليس Mini Experience Modal
    if (this.effectiveLevel() === 'beginner') return false;
    if (this.getRoleBasedTasks(this.effectiveRole(), this.effectiveLevel()).length === 0) {
      return false;
    }
    const key = this.getStage4StorageKey();
    if (!key) return false;
    return localStorage.getItem(key) !== 'true';
  }

  /** تعليم المرحلة 4 كمكتملة (بعد تخطي أو إنهاء) */
  markStage4Complete(): void {
    try {
      const key = this.getStage4StorageKey();
      if (key) localStorage.setItem(key, 'true');
    } catch {
      /* ignore */
    }
    this.setStage4QuickStartExitSuspended(false);
  }

  /**
   * مهام المرحلة 4 حسب الدور؛ لصاحب المنشأة والمحاسب والتقني تختلف حسب مستوى الخبرة.
   * تُصفّى حسب صلاحيات المستخدم الحالي (لا تُعرض مهام بدون وصول).
   */
  getRoleBasedTasks(role: UserRole, experienceLevel?: ExperienceLevel): Stage4Task[] {
    let tasks: Stage4Task[];
    if (role === 'owner') {
      const level = experienceLevel ?? this.effectiveLevel();
      if (level === 'expert') {
        tasks = [...OWNER_STAGE4_TASKS_EXPERT];
      } else {
        tasks = [...OWNER_STAGE4_TASKS_FULL];
      }
    } else if (role === 'accountant') {
      const level = experienceLevel ?? this.effectiveLevel();
      if (level === 'beginner') {
        tasks = [...ACCOUNTANT_STAGE4_TASKS_BEGINNER];
      } else if (level === 'expert') {
        tasks = [...ACCOUNTANT_STAGE4_TASKS_EXPERT];
      } else {
        tasks = [...ACCOUNTANT_STAGE4_TASKS_INTERMEDIATE];
      }
    } else if (role === 'technician') {
      const level = experienceLevel ?? this.effectiveLevel();
      if (level === 'beginner') {
        tasks = [...TECHNICIAN_STAGE4_TASKS_BEGINNER];
      } else if (level === 'expert') {
        tasks = [...TECHNICIAN_STAGE4_TASKS_EXPERT];
      } else {
        tasks = [...TECHNICIAN_STAGE4_TASKS_INTERMEDIATE];
      }
    } else {
      tasks = [...(ROLE_TASKS[role] ?? OWNER_STAGE4_TASKS_FULL)];
    }
    return tasks.filter(
      (t) => !t.permissionKey || this.permission.hasPermission(t.permissionKey)
    );
  }

  private getStage4TasksStorageKey(): string | null {
    const base = this.getStorageKey();
    if (!base) return null;
    return `${base}${STAGE4_TASKS_KEY_SUFFIX}`;
  }

  /** المهام المنجزة في المرحلة 4 */
  getCompletedTasks(): string[] {
    try {
      const key = this.getStage4TasksStorageKey();
      if (!key) return [];
      const raw = localStorage.getItem(key);
      if (!raw) return [];
      const parsed = JSON.parse(raw) as string[];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /** تعليم مهمة كمكتملة */
  markTaskDone(taskId: string): void {
    try {
      const key = this.getStage4TasksStorageKey();
      if (!key) return;
      const done = this.getCompletedTasks();
      if (!done.includes(taskId)) {
        done.push(taskId);
        localStorage.setItem(key, JSON.stringify(done));
      }
    } catch {
      /* ignore */
    }
  }

  /** عدد المهام المنجزة في المرحلة 4 (فقط المهام الخاصة بالدور الحالي) */
  getCompletedTasksCount(): number {
    const tasks = this.getRoleBasedTasks(this.effectiveRole(), this.effectiveLevel());
    const taskIds = new Set(tasks.map((t) => t.id));
    return this.getCompletedTasks().filter((id) => taskIds.has(id)).length;
  }

  /** نسبة إكمال المعالج (0–100). المرحلة 1 = 50%، المرحلة 4 = 50% موزعة على المهام */
  getCompletionPercentage(): number {
    if (!this.isStage1Complete()) return 0;
    const stage1Weight = 0.5;
    const stage4Weight = 0.5;
    const tasks = this.getRoleBasedTasks(this.effectiveRole(), this.effectiveLevel());
    const totalTasks = tasks.length;
    if (totalTasks === 0) return Math.round(stage1Weight * 100);
    const doneCount = this.getCompletedTasksCount();
    const stage4Progress = Math.min(doneCount / totalTasks, 1);
    const pct = (stage1Weight + stage4Weight * stage4Progress) * 100;
    return Math.round(Math.min(pct, 100));
  }

  /** هل يجب عرض رسالة الترحيب عند العودة؟ (عند فتح المرحلة 4 من تحميل الصفحة وليس من المرحلة 1) */
  shouldShowWelcomeBack(): boolean {
    return this.isStage1Complete() && this.shouldShowStage4();
  }

  private getStage4StorageKey(): string | null {
    const base = this.getStorageKey();
    if (!base) return null;
    return `${base}${STAGE4_KEY_SUFFIX}`;
  }

  /** الدور الفعلي للاستخدام (مع افتراضي "صاحب منشأة") */
  readonly effectiveRole = computed(() => this.stage1Signal()?.role ?? DEFAULT_ROLE);

  /** المستوى الفعلي للاستخدام (مع افتراضي "متوسط") */
  readonly effectiveLevel = computed(() => this.stage1Signal()?.experienceLevel ?? DEFAULT_LEVEL);

  private getStorageKey(): string | null {
    const token = this.authService.getAccessToken();
    if (!token) return null;
    const userId = this.jwtService.getUserId(token);
    if (!userId) return null;
    return `${STORAGE_KEY_PREFIX}${userId}`;
  }

  private getLegacyStorageKey(): string | null {
    const token = this.authService.getAccessToken();
    if (!token) return null;
    const userId = this.jwtService.getUserId(token);
    if (!userId) return null;
    return `${LEGACY_KEY_PREFIX}${userId}`;
  }

  private getCoachmarksSkipKey(): string | null {
    const token = this.authService.getAccessToken();
    if (!token) return null;
    const userId = this.jwtService.getUserId(token);
    if (!userId) return null;
    return `${COACHMARKS_SKIP_KEY_PREFIX}${userId}`;
  }

  private getOwnerMandatoryFlowSuspendedKey(): string | null {
    const token = this.authService.getAccessToken();
    if (!token) return null;
    const userId = this.jwtService.getUserId(token);
    if (!userId) return null;
    return `${OWNER_MANDATORY_FLOW_SUSPENDED_PREFIX}${userId}`;
  }

  private loadOwnerMandatorySuspendedFromStorage(): boolean {
    try {
      const key = this.getOwnerMandatoryFlowSuspendedKey();
      return key ? localStorage.getItem(key) === '1' : false;
    } catch {
      return false;
    }
  }

  private getStage4QuickStartExitSuspendedKey(): string | null {
    const token = this.authService.getAccessToken();
    if (!token) return null;
    const userId = this.jwtService.getUserId(token);
    if (!userId) return null;
    return `${STAGE4_QUICKSTART_EXIT_SUSPENDED_PREFIX}${userId}`;
  }

  private loadStage4QuickStartExitSuspendedFromStorage(): boolean {
    try {
      const key = this.getStage4QuickStartExitSuspendedKey();
      return key ? localStorage.getItem(key) === '1' : false;
    } catch {
      return false;
    }
  }

  private loadInitialBootstrap(): { stage1: Stage1Data | null; coachmarksSkipped: boolean } {
    const stage1 = this.resolveStage1FromStorage();
    const skipKey = this.getCoachmarksSkipKey();
    if (stage1) {
      try {
        if (skipKey) localStorage.removeItem(skipKey);
      } catch {
        /* ignore */
      }
      return { stage1, coachmarksSkipped: false };
    }
    const skipped = !!(skipKey && localStorage.getItem(skipKey) === '1');
    return { stage1: null, coachmarksSkipped: skipped };
  }

  private resolveStage1FromStorage(): Stage1Data | null {
    try {
      const key = this.getStorageKey();
      if (!key) return null;

      const raw = localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw) as Stage1Data;
        if (parsed?.role && this.isValidRole(parsed.role)) {
          return {
            role: parsed.role,
            experienceLevel: this.isValidLevel(parsed.experienceLevel)
              ? parsed.experienceLevel
              : DEFAULT_LEVEL,
          };
        }
      }

      const legacyKey = this.getLegacyStorageKey();
      if (legacyKey) {
        const legacyRaw = localStorage.getItem(legacyKey);
        if (legacyRaw && this.isValidLevel(legacyRaw as ExperienceLevel)) {
          const migrated: Stage1Data = {
            role: DEFAULT_ROLE,
            experienceLevel: legacyRaw as ExperienceLevel,
          };
          this.persist(migrated);
          try {
            localStorage.removeItem(legacyKey);
          } catch {
            /* ignore */
          }
          return migrated;
        }
      }

      return null;
    } catch {
      return null;
    }
  }

  private clearCoachmarksSkipStorageAndSignal(): void {
    try {
      const skipKey = this.getCoachmarksSkipKey();
      if (skipKey) localStorage.removeItem(skipKey);
    } catch {
      /* ignore */
    }
    this.coachmarksSkippedSignal.set(false);
  }

  private setCoachmarksSkippedPersisted(): void {
    try {
      const skipKey = this.getCoachmarksSkipKey();
      if (skipKey) localStorage.setItem(skipKey, '1');
    } catch {
      /* ignore */
    }
    this.coachmarksSkippedSignal.set(true);
  }

  private isValidRole(r: string): r is UserRole {
    return ['owner', 'accountant', 'technician', 'trainee'].includes(r);
  }

  private isValidLevel(l: string): l is ExperienceLevel {
    return ['beginner', 'intermediate', 'expert'].includes(l);
  }

  /** هل يجب عرض معالج المرحلة 1 (الدور + المستوى)؟ */
  shouldShowExperienceSelector(): boolean {
    if (this.coachmarksSkippedSignal()) return false;
    const key = this.getStorageKey();
    if (!key) return false;
    if (localStorage.getItem(key)) return false;
    const legacyKey = this.getLegacyStorageKey();
    if (legacyKey && localStorage.getItem(legacyKey)) return false;
    return true;
  }

  /** حفظ المرحلة 1 (الدور إلزامي؛ المستوى إلزامي في الواجهة لغير المتدرب، ويُفترض متوسط عند التخزين بدون قيمة) */
  setStage1(role: UserRole, experienceLevel?: ExperienceLevel): void {
    this.clearCoachmarksSkipStorageAndSignal();
    const data: Stage1Data = {
      role,
      experienceLevel: experienceLevel ?? DEFAULT_LEVEL,
    };
    this.stage1Signal.set(data);
    this.persist(data);
  }

  /** حفظ المستوى فقط (للتوافق مع الاستخدام القديم) */
  setExperienceLevel(level: ExperienceLevel): void {
    const current = this.stage1Signal();
    const data: Stage1Data = {
      role: current?.role ?? DEFAULT_ROLE,
      experienceLevel: level,
    };
    this.stage1Signal.set(data);
    this.persist(data);
  }

  /**
   * تخطي مودال تخصيص التجربة: لا يُحفظ دور/مستوى وهمي؛ تُعطَّل كل المعالجات الإرشادية
   * حتى يفعّلها المستخدم من «إدارة المعالج الذكي» أو يعيد فتح التخصيص.
   */
  applyDefaultAndSkip(): void {
    try {
      const key = this.getStorageKey();
      if (key) localStorage.removeItem(key);
      const legacyKey = this.getLegacyStorageKey();
      if (legacyKey) localStorage.removeItem(legacyKey);
    } catch {
      /* ignore */
    }
    this.stage1Signal.set(null);
    this.setCoachmarksSkippedPersisted();
  }

  /** إعادة تعيين المرحلة 1 و4 ثم طلب إعادة عرض مودال التخصيص (يستدعيه الـ Layout) */
  requestReopenStage1Modal(): void {
    this.reset();
    this.reopenStage1Subject.next();
  }

  /** إعادة تعيين (لإعادة تشغيل المعالج من الإعدادات) */
  reset(): void {
    try {
      const key = this.getStorageKey();
      const legacyKey = this.getLegacyStorageKey();
      const stage4Key = this.getStage4StorageKey();
      const stage4TasksKey = this.getStage4TasksStorageKey();
      const skipKey = this.getCoachmarksSkipKey();
      const mandatorySuspKey = this.getOwnerMandatoryFlowSuspendedKey();
      const stage4ExitSuspKey = this.getStage4QuickStartExitSuspendedKey();
      if (key) localStorage.removeItem(key);
      if (legacyKey) localStorage.removeItem(legacyKey);
      if (stage4Key) localStorage.removeItem(stage4Key);
      if (stage4TasksKey) localStorage.removeItem(stage4TasksKey);
      if (skipKey) localStorage.removeItem(skipKey);
      if (mandatorySuspKey) localStorage.removeItem(mandatorySuspKey);
      if (stage4ExitSuspKey) localStorage.removeItem(stage4ExitSuspKey);
      this.stage1Signal.set(null);
      this.coachmarksSkippedSignal.set(false);
      this.ownerMandatoryFlowSuspendedSignal.set(false);
      this.stage4QuickStartExitSuspendedSignal.set(false);
    } catch {
      this.stage1Signal.set(null);
      this.coachmarksSkippedSignal.set(false);
      this.ownerMandatoryFlowSuspendedSignal.set(false);
      this.stage4QuickStartExitSuspendedSignal.set(false);
    }
  }

  private persist(data: Stage1Data): void {
    try {
      const key = this.getStorageKey();
      if (key) {
        localStorage.setItem(key, JSON.stringify(data));
      }
    } catch {
      /* ignore */
    }
  }

  /** المفاتيح المحفوظة عند تسجيل الخروج (للدمج مع AuthService) */
  static getStorageKeyPrefix(): string {
    return STORAGE_KEY_PREFIX;
  }

  static getLegacyStorageKeyPrefix(): string {
    return LEGACY_KEY_PREFIX;
  }
}
